import {
  completeRewardRedemption,
  createRewardRedemption,
  fulfillPhysicalGoodRedemption,
} from '../../api/Rest/rewards';
import Repository, { Cursor, RepositoryJson } from '../../models/Repository';
import { AsyncActionOptions, FetchActionResult } from '../../models/Store';
import { PartialPick, SecondParameter } from '../../models/Utils';
import { Uuid } from '../Base';
import { Profile, ResetPasswordResult } from '../UserStore';

import {
  RewardCategory,
  RewardCategoryJson,
  RewardPlaform,
} from './RewardCategory';
import { RewardOffer, RewardOfferJson } from './RewardOffer';
import {
  RewardRedemption,
  RewardRedemptionJson,
  RewardRedemptionTabState,
} from './RewardRedemption';
import {
  ReferralSystem,
  RewardReferral,
  RewardReferralCode,
  RewardReferralJson,
} from './RewardReferral';

export type RewardCategoryMapField = string;
export type RewardCategoryListField = string; // store

export class RewardCategoryRepository extends Repository<
  RewardCategory,
  RewardCategoryJson<Date | number>,
  RewardCategoryMapField,
  RewardCategoryListField
> {
  constructor(
    repository?: RepositoryJson<
      RewardCategoryJson<Date | number>,
      RewardCategoryMapField,
      RewardCategoryListField
    >,
  ) {
    super({ ...repository }, (x) => new RewardCategory(x));
  }
}

export enum RewardOfferMapType {
  ActiveUser = 'activeUser',
}
export type RewardOfferMapField = RewardOfferMapType;
export type RewardOfferListField = string; // store_rewardCategoryId(_admin)

export class RewardOfferRepository extends Repository<
  RewardOffer,
  RewardOfferJson<Date | number>,
  RewardOfferMapField,
  RewardOfferListField
> {
  constructor(
    repository?: RepositoryJson<
      RewardOfferJson<Date | number>,
      RewardOfferMapField,
      RewardOfferListField
    >,
  ) {
    super(
      {
        ...repository,
        mapFields: Repository.initFields(repository?.mapFields, {
          [RewardOfferMapType.ActiveUser]: {},
        }),
      },
      (x) => new RewardOffer(x),
    );
  }
}

export type RewardRedemptionMapField = string;
export type RewardRedemptionListField = string; // store_tabState(_admin) / store_offerId

export class RewardRedemptionRepository extends Repository<
  RewardRedemption,
  RewardRedemptionJson<Date | number>,
  RewardRedemptionMapField,
  RewardRedemptionListField
> {
  constructor(
    repository?: RepositoryJson<
      RewardRedemptionJson<Date | number>,
      RewardRedemptionMapField,
      RewardRedemptionListField,
      Date | number
    >,
  ) {
    super({ ...repository }, (x) => new RewardRedemption(x));
  }
}

export enum RewardReferralType {
  Referral = 'referral',
  SecondReferral = 'secondReferral',
}

export type RewardReferralMapField = string;
export type RewardReferralListField = string;
export class RewardReferralRepository extends Repository<
  RewardReferral,
  RewardReferralJson<Date | number>,
  RewardReferralMapField,
  RewardReferralListField
> {
  constructor(
    repository?: RepositoryJson<
      RewardReferralJson<Date | number>,
      RewardReferralMapField,
      RewardReferralListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [RewardReferralType.Referral]: {},
          [RewardReferralType.SecondReferral]: {},
        }),
        mapFields: Repository.initFields(repository?.mapFields, {
          [RewardReferralType.Referral]: {},
          [RewardReferralType.SecondReferral]: {},
        }),
      },
      (x) => new RewardReferral(x),
    );
  }
}

export type RewardStoreState = {
  categories: RewardCategoryRepository;
  offers: RewardOfferRepository;
  redemptions: RewardRedemptionRepository;
  referrals: RewardReferralRepository;
};

export type RewardStoreAction = {
  // category
  getCategories: (fieldName: RewardCategoryListField) => RewardCategory[];
  getCategory: (rewardCategoryId: string) => RewardCategory | undefined | null;
  fetchCategories: (
    store: RewardPlaform,
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  createCategory: (
    store: RewardPlaform,
    name: string,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  updateCategory: (
    store: RewardPlaform,
    rewardCategoryId: string,
    body: PartialPick<RewardCategoryJson, 'i18nRewardCategoryInfo' | 'ordinal'>,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  deleteCategory: (
    store: RewardPlaform,
    rewardCategoryId: string,
    relocationCategoryId?: string,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  // offer
  getOffers: (fieldName: RewardOfferListField) => RewardOffer[];
  getOffer: (rewardOfferId: string) => RewardOffer | undefined | null;
  fetchRecentOffers: (
    store: RewardPlaform,
    leaderboardId?: string,
    options?: AsyncActionOptions,
  ) => Promise<void>;
  fetchOffers: (
    // categories should be fetched before call this
    store: RewardPlaform,
    params: {
      leaderboardId?: string;
      rewardCategoryId?: string;
      referralSystem?: ReferralSystem;
    },
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  fetchOffersByAdmin: (
    // remember to add listener before call this
    store: RewardPlaform,
    params: {
      leaderboardId?: string;
      rewardCategoryId: string;
    },
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  fetchOffer: (
    store: RewardPlaform,
    rewardOfferId: string,
    options?: AsyncActionOptions,
  ) => Promise<void>;
  fetchOfferByRedemptionId: (
    // redemption should be fetched before call this
    rewardRedemptionId: string,
    store: RewardPlaform,
    options?: AsyncActionOptions,
  ) => Promise<void>;
  // redemption
  getRedemptions: (fieldName: RewardRedemptionListField) => RewardRedemption[];
  getRedemption: (
    rewardRedemptionId: string,
  ) => RewardRedemption | undefined | null;
  getRedemptionsByOfferId: (
    offerId: string,
    store: RewardPlaform,
  ) => RewardRedemption[];
  fetchRedemptions: (
    store: RewardPlaform,
    tabState: RewardRedemptionTabState,
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  fetchRedemptionsByAdmin: (
    store: RewardPlaform,
    state: RewardRedemptionTabState,
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  fetchRedemptionsByRedeemedBy: (
    store: RewardPlaform,
    state: RewardRedemptionTabState,
    redeemedBy: Uuid,
    referralSystem: ReferralSystem,
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  fetchRedemptionsByOfferId: (
    store: RewardPlaform,
    offerId: string,
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  fetchLastRecipient: (
    store: RewardPlaform,
    options?: AsyncActionOptions,
  ) => Promise<RewardRedemption | undefined>;
  fetchRedemption: (
    store: RewardPlaform,
    rewardRedemptionId: Uuid,
    options?: AsyncActionOptions,
  ) => Promise<RewardRedemption | undefined>;
  createRedemption: (
    store: RewardPlaform,
    body: SecondParameter<typeof createRewardRedemption>,
    options?: AsyncActionOptions,
  ) => Promise<RewardRedemption | undefined>;
  captureRedemptionPayment: (
    store: RewardPlaform,
    rewardRedemptionId: string,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  deliverPhysicalGood: (
    store: RewardPlaform,
    rewardRedemptionId: string,
    body: SecondParameter<typeof fulfillPhysicalGoodRedemption>,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  extendCompletion: (
    store: RewardPlaform,
    rewardRedemptionId: string,
    durationInMs: number,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  completeRedemption: (
    store: RewardPlaform,
    rewardRedemptionId: string,
    body: SecondParameter<typeof completeRewardRedemption>,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  remindRedemption: (
    store: RewardPlaform,
    rewardRedemptionId: string,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  cancelRedemption: (
    store: RewardPlaform,
    rewardRedemptionId: string,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  cancelWebPlanSubscription: (
    store: RewardPlaform,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;
  manageWebPlanSubscription: (
    store: RewardPlaform,
    options?: AsyncActionOptions,
  ) => Promise<string | undefined>;
  getRewardReferralCode: (
    referralSystem: ReferralSystem,
  ) => Promise<RewardReferralCode | undefined>;
  createRewardReferral: (
    path: { referralSystem: ReferralSystem },
    params: { referralCode: string },
    options?: AsyncActionOptions,
  ) => Promise<RewardReferral | undefined>;
  referralCodeValidation: (
    path: { referralSystem: ReferralSystem },
    params: { referralCode: string },
    options?: AsyncActionOptions,
  ) => Promise<ResetPasswordResult | Profile>;
  getRewardReferrals: (fieldName: string) => RewardReferral[];
  getRewardReferral: (userId: string) => RewardReferral | null | undefined;
  fetchRewardReferrals: (
    params: {
      referralSystem: ReferralSystem;
      fieldName: RewardReferralType | string;
      referredBy?: Uuid;
    },
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  fetchRewardReferral: (
    params: { referralSystem: ReferralSystem },
    options?: AsyncActionOptions,
  ) => Promise<void>;
  fetchRewardReferralUser: (
    referralSystem: ReferralSystem,
    userId: string,
    options?: AsyncActionOptions,
  ) => Promise<void>;
  // prune listener
  onCategoryPrune: (category: RewardCategory) => void;
  onOfferPrune: (offer: RewardOffer) => void;
  onRedemptionPrune: (redemption: RewardRedemption) => void;
};
